import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ListChecks, Plus, Trash2, MapPin, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function BucketList({ eventId, isOwner }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "travel",
    added_by: ""
  });

  useEffect(() => {
    fetchBucketList();
  }, [eventId]);

  const fetchBucketList = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/bucket-list`);
      setItems(response.data);
    } catch (error) {
      console.error("Failed to fetch bucket list:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.added_by) {
      toast.error("Titel und Name sind erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/bucket-list`,
        formData
      );
      
      toast.success("Zur Bucket List hinzugefügt!");
      setFormData({ title: "", description: "", category: "travel", added_by: "" });
      fetchBucketList();
    } catch (error) {
      console.error("Failed to add item:", error);
      toast.error("Fehler beim Hinzufügen");
    }
  };

  const handleToggle = async (itemId, completed) => {
    try {
      await axios.patch(
        `${API}/events/${eventId}/bucket-list/${itemId}`,
        { completed: !completed },
        { withCredentials: true }
      );
      fetchBucketList();
    } catch (error) {
      console.error("Failed to toggle item:", error);
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm("Wirklich löschen?")) return;
    
    try {
      await axios.delete(
        `${API}/events/${eventId}/bucket-list/${itemId}`,
        { withCredentials: true }
      );
      toast.success("Gelöscht");
      fetchBucketList();
    } catch (error) {
      console.error("Failed to delete item:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  const completedCount = items.filter(i => i.completed).length;
  const categories = [
    { value: "travel", label: "🌍 Reisen", icon: "🌍" },
    { value: "adventure", label: "🎢 Abenteuer", icon: "🎢" },
    { value: "learning", label: "📚 Lernen", icon: "📚" },
    { value: "wellness", label: "💆 Wellness", icon: "💆" },
    { value: "relationships", label: "❤️ Beziehungen", icon: "❤️" },
    { value: "other", label: "✨ Sonstiges", icon: "✨" }
  ];

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <ListChecks className="w-5 h-5 text-primary" />
          Bucket List
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Gemeinsame Träume und Ziele sammeln
        </p>

        {items.length > 0 && (
          <div className="p-3 bg-secondary/20 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span>{completedCount} von {items.length} erreicht</span>
              <span className="font-bold">{items.length > 0 ? Math.round((completedCount / items.length) * 100) : 0}%</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden mt-2">
              <div 
                className="h-full bg-green-500 transition-all duration-500"
                style={{ width: `${items.length > 0 ? (completedCount / items.length) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Add Item Form */}
      <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4 bg-secondary/20">
        <div>
          <Label>Was möchtet ihr erleben? *</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="z.B. Nordlichter sehen, Marathon laufen"
            required
          />
        </div>

        <div>
          <Label>Beschreibung (optional)</Label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Details..."
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Kategorie</Label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border rounded-md bg-background"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Dein Name *</Label>
            <Input
              value={formData.added_by}
              onChange={(e) => setFormData({ ...formData, added_by: e.target.value })}
              placeholder="Name"
              required
            />
          </div>
        </div>

        <Button type="submit" className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          Zur Bucket List hinzufügen
        </Button>
      </form>

      {/* Items List */}
      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <ListChecks className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Eure Bucket List ist noch leer</p>
            <p className="text-xs mt-1">Fügt eure Träume und Ziele hinzu!</p>
          </div>
        ) : (
          items.map((item) => {
            const category = categories.find(c => c.value === item.category);
            
            return (
              <div 
                key={item.id} 
                className={`p-4 rounded-lg border transition-all ${
                  item.completed ? 'bg-green-50 border-green-200' : 'bg-card'
                }`}
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => handleToggle(item.id, item.completed)}
                    className="mt-1 flex-shrink-0"
                    disabled={!isOwner}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      item.completed 
                        ? 'bg-green-500 border-green-500' 
                        : 'border-muted-foreground'
                    }`}>
                      {item.completed && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                  
                  <div className="flex-1">
                    <div className="flex items-start gap-2 mb-1">
                      <span className="text-lg">{category?.icon}</span>
                      <div className="flex-1">
                        <p className={`font-medium ${
                          item.completed ? 'line-through text-muted-foreground' : ''
                        }`}>
                          {item.title}
                        </p>
                        {item.description && (
                          <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">von {item.added_by}</p>
                      </div>
                    </div>
                  </div>

                  {isOwner && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(item.id)}
                      className="h-8 w-8 text-destructive flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}